import { useState } from 'react';
import { RechtsLinks } from '../../rechtliches/komponenten/RechtsRahmen';
import { Link } from 'react-router-dom';
import { useAuth } from '../../../gemeinsam/lib/AuthContext';
import type { ZweiterFaktor } from '../../../gemeinsam/lib/AuthContext';
import { ApiFehler } from '../../../gemeinsam/lib/api';
import { Feld, Hinweis, Knopf } from '../../../gemeinsam/komponenten/Basis';
import { LogoKachel } from '../../firma/komponenten/Logo';
import { useSprache, SPRACHEN } from '../../../gemeinsam/lib/i18n';
import { ANBIETER_URL, POWERED_BY, PRODUKT } from '../../../gemeinsam/lib/marke';
import type { SprachCode } from '../../../gemeinsam/lib/i18n';

export default function Anmelden() {
  const { anmelden, codePruefen, abmeldegrund } = useAuth();
  const { t, sprache, setSprache } = useSprache();
  const [kennung, setKennung] = useState('');
  const [passwort, setPasswort] = useState('');
  const [fehler, setFehler] = useState<string | null>(null);
  const [laedt, setLaedt] = useState(false);
  // Ist die Zwei-Faktor-Anmeldung aktiv, kommt hier der Vorgang an und die
  // Maske schaltet auf den zweiten Schritt um.
  const [zweiterFaktor, setZweiterFaktor] = useState<ZweiterFaktor | null>(null);
  const [code, setCode] = useState('');
  // Bei der Authenticator-App: statt des Codes einen der Ersatzcodes eintippen.
  const [ersatz, setErsatz] = useState(false);
  const codeFertig = ersatz ? code.replace(/[^A-Za-z0-9]/g, '').length === 8 : code.length === 6;

  async function absenden(e: React.FormEvent) {
    e.preventDefault();
    setFehler(null);
    setLaedt(true);
    try {
      const weiter = await anmelden(kennung.trim(), passwort);
      if (weiter) {
        setZweiterFaktor(weiter);
        // Das Passwort wird nicht mehr gebraucht und soll nicht herumliegen.
        setPasswort('');
      }
    } catch (e) {
      // Gesperrte Zugänge bekommen die Begründung des Backends zu sehen,
      // falsche Zugangsdaten bleiben bewusst vage.
      setFehler(e instanceof ApiFehler ? e.message : t('anmelden.fehler'));
    } finally {
      setLaedt(false);
    }
  }

  async function codeAbsenden(e: React.FormEvent) {
    e.preventDefault();
    if (!zweiterFaktor) return;
    setFehler(null);
    setLaedt(true);
    try {
      await codePruefen(zweiterFaktor.vorgangId, code.trim());
    } catch (e) {
      setFehler(e instanceof ApiFehler ? e.message : t('anmelden.fehler'));
      // Nach einem verbrauchten Vorgang hilft nur ein neuer Anlauf.
      if (e instanceof ApiFehler && (e.daten?.code === 'ABGELAUFEN' || e.daten?.code === 'ZU_VIELE_VERSUCHE')) {
        setZweiterFaktor(null);
        setCode('');
        setErsatz(false);
      }
    } finally {
      setLaedt(false);
    }
  }

  return (
    <div
      style={{
        // Oben die Statusleiste, unten die Home-Leiste freihalten; im Browser
        // sind beide Werte 0 und es bleibt bei den 3rem von vorher.
        paddingTop: 'calc(3rem + var(--sicher-oben))',
        paddingBottom: 'calc(3rem + var(--sicher-unten))',
      }}
      className="flex min-h-full items-center justify-center bg-gradient-to-b from-slate-100 to-slate-200 px-4 dark:from-slate-950 dark:to-slate-900">
      <div className="w-full max-w-sm">
        <div className="mb-4 flex justify-end">
          <select
            value={sprache}
            onChange={e => setSprache(e.target.value as SprachCode)}
            aria-label={t('allg.sprache')}
            className="rounded-lg border border-slate-300 bg-white/80 px-2 py-1.5 text-sm text-slate-900 dark:border-slate-700 dark:bg-slate-900/80 dark:text-slate-100"
          >
            {SPRACHEN.map(s2 => <option key={s2.code} value={s2.code}>{s2.flagge} {s2.name}</option>)}
          </select>
        </div>

        <div className="mb-8 text-center">
          <LogoKachel groesse="gross" klasse="mx-auto mb-4 shadow-lg shadow-marke-600/25" />
          <h1 className="text-3xl font-bold text-slate-900 dark:text-slate-100">{t('app.name')}</h1>
          <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">{t('app.untertitel')}</p>
        </div>

        <form
          onSubmit={zweiterFaktor ? codeAbsenden : absenden}
          className="space-y-4 rounded-xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-800 dark:bg-slate-900"
        >
          {zweiterFaktor ? (
            <>
              <div>
                <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">{t('zfa.titel')}</h2>
                {zweiterFaktor.methode === 'app' ? (
                  <p className="text-sm text-slate-500 dark:text-slate-400">{t(ersatz ? 'zfa.ersatzText' : 'zfa.appText')}</p>
                ) : (
                  <p className="text-sm text-slate-500 dark:text-slate-400">
                    {t('zfa.gesendet')} <strong className="text-slate-700 dark:text-slate-200">{zweiterFaktor.mailMaske}</strong>
                  </p>
                )}
              </div>

              {fehler && <Hinweis art="fehler">{fehler}</Hinweis>}

              <Feld
                key={ersatz ? 'ersatz' : 'code'}
                label={t(zweiterFaktor.methode === 'app' ? (ersatz ? 'zfa.ersatzCode' : 'zfa.appCode') : 'zfa.code')}
                value={code}
                onChange={e => setCode(ersatz
                  ? e.target.value.toUpperCase().replace(/[^A-Z0-9-]/g, '').slice(0, 9)
                  : e.target.value.replace(/\D/g, '').slice(0, 6))}
                inputMode={ersatz ? 'text' : 'numeric'}
                autoComplete={ersatz ? 'off' : 'one-time-code'}
                placeholder={ersatz ? 'XXXX-XXXX' : undefined}
                autoFocus
                required
                hinweis={zweiterFaktor.methode === 'mail' ? t('zfa.gueltig').replace('{min}', String(zweiterFaktor.gueltigMinuten)) : undefined}
                className="text-center text-2xl tracking-[0.3em]"
              />

              <Knopf type="submit" variante="primaer" laedt={laedt} className="w-full"
                     disabled={!codeFertig}>
                {t('zfa.bestaetigen')}
              </Knopf>

              {zweiterFaktor.methode === 'app' && (
                <button type="button" onClick={() => { setErsatz(x => !x); setCode(''); setFehler(null); }}
                        className="w-full text-center text-xs text-marke-700 hover:underline dark:text-marke-300">
                  {t(ersatz ? 'zfa.appCodeNutzen' : 'zfa.ersatzNutzen')}
                </button>
              )}

              <button type="button"
                      onClick={() => { setZweiterFaktor(null); setCode(''); setErsatz(false); setFehler(null); }}
                      className="w-full text-center text-xs text-slate-500 hover:underline dark:text-slate-400">
                {t('zfa.andererZugang')}
              </button>
            </>
          ) : (
            <>
            <div>
              <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">{t('anmelden.willkommen')}</h2>
              <p className="text-sm text-slate-500 dark:text-slate-400">{t('anmelden.hinweis')}</p>
            </div>

            {abmeldegrund && !fehler && <Hinweis art="warnung">{abmeldegrund}</Hinweis>}
            {fehler && <Hinweis art="fehler">{fehler}</Hinweis>}

            <Feld
              label={t('anmelden.kennung')}
              value={kennung}
              onChange={e => setKennung(e.target.value)}
              autoComplete="username"
              autoFocus
              required
            />
            <Feld
              label={t('anmelden.passwort')}
              type="password"
              value={passwort}
              onChange={e => setPasswort(e.target.value)}
              autoComplete="current-password"
              required
            />
            <div className="-mt-2 text-right">
              <Link to="/passwort-vergessen" className="text-sm font-medium text-marke-600 hover:underline dark:text-marke-400">
                {t('passwort.vergessenLink')}
              </Link>
            </div>

            <Knopf type="submit" variante="primaer" laedt={laedt} className="w-full">
              {t('aktion.anmelden')}
            </Knopf>
            </>
          )}
        </form>

        {/* In der App ist dies der erste Bildschirm — ohne Startseite gäbe es
            sonst keinen Weg zur Registrierung. Beim zweiten Schritt der
            Zwei-Faktor-Anmeldung stört der Link, dort bleibt er weg. */}
        {!zweiterFaktor && (
          <p className="mt-5 text-center text-sm text-slate-600 dark:text-slate-400">
            {t('anmelden.keinKonto')}{' '}
            <Link to="/registrieren" className="font-medium text-marke-600 hover:underline dark:text-marke-400">
              {t('anmelden.jetztRegistrieren')}
            </Link>
          </p>
        )}

        <RechtsLinks klasse="mt-6" />
        <p className="mt-3 text-center text-xs text-slate-500 dark:text-slate-400">
          {PRODUKT} © {new Date().getFullYear()}
          <a href={ANBIETER_URL} target="_blank" rel="noopener noreferrer"
             className="mt-0.5 block hover:underline">{POWERED_BY}</a>
        </p>
      </div>
    </div>
  );
}
